import { DragDropPosition } from '@portal-shared/app/models/drag-drop-position.model';
import { DropPayload } from '@portal-shared/app/models/drop-payload.model';
import { BehaviorSubject, Subject, filter, firstValueFrom } from 'rxjs';
import { BaseDraggingService } from './base-dragging.service';

export abstract class PortalDraggingService<TDragDropPayload, TDragOverPayload> extends BaseDraggingService {
  private dragOverPayload$ = new Subject<DropPayload<TDragDropPayload, TDragOverPayload>>();
  private dropPayload?: DropPayload<TDragDropPayload, TDragOverPayload>;
  private dropPayload$ = new Subject<DropPayload<TDragDropPayload, TDragOverPayload>>();

  protected _isDragging = false;
  public get isDragging() {
    return this._isDragging;
  }
  public set isDragging(value: boolean) {
    this._isDragging = value;
    this.isDragging$.next(value);
  }

  protected isDragging$ = new BehaviorSubject<boolean>(this.isDragging);
  protected dragEvent$ = new Subject<DragDropPosition>();

  public listen() {
    document.addEventListener('mouseleave', () => {
      this.endDragging();
    });

    document.addEventListener('touchend', () => {
      this.endDragging();
    });

    document.addEventListener('touchcancel', () => {
      this.endDragging();
    });
    document.addEventListener('mouseup', () => {
      this.endDragging();
    });

    document.addEventListener('mousemove', e => {
      if (!this.isDragging) {
        return;
      }

      this.dragEvent$.next(this.toPositionMouse(e));
    });
    document.addEventListener('touchmove', e => {
      if (!this.isDragging) {
        return;
      }
      this.dragEvent$.next(this.toPositionTouch(e));
    });
  }

  private endDragging() {
    if (!this.isDragging) {
      return;
    }
    this.isDragging = false;
    if (this.dropPayload) {
      this.dropPayload$.next(this.dropPayload);
    }
  }

  public startDragging(payload?: TDragDropPayload) {
    this.isDragging = true;
    this.dropPayload = undefined;

    if (payload) {
      this.dropPayload = {
        dragged: payload,
        draggedOver: {}
      };
    }
    return this.observeDragPosition();
  }

  public setDragOverPayload(payload: TDragOverPayload) {
    if (!this.dropPayload) {
      this.dropPayload = {
        dragged: {}
      };
    }

    this.dropPayload.draggedOver = payload;
    this.dragOverPayload$.next(this.dropPayload);
  }

  protected toPositionTouch(e: TouchEvent): DragDropPosition {
    const firstTouch = e.touches ? e.touches[0] : (e as unknown as { clientX: number; clientY: number }); //Cypress does not correctly assign touches https://github.com/cypress-io/cypress/issues/3808
    return {
      x: firstTouch.clientX,
      y: firstTouch.clientY,
      target: document.elementFromPoint(firstTouch.clientX, firstTouch.clientY) as HTMLElement
    };
  }

  protected toPositionMouse(e: MouseEvent): DragDropPosition {
    return {
      x: e.clientX,
      y: e.clientY,
      target: document.elementFromPoint(e.clientX, e.clientY) as HTMLElement
    };
  }

  public observeDragPayload() {
    return this.dragOverPayload$.asObservable();
  }

  public observeDropPayload() {
    return this.dropPayload$.asObservable();
  }

  public observeIsDragging() {
    return this.isDragging$.asObservable();
  }

  public observeDragPosition() {
    return this.dragEvent$.asObservable();
  }

  public async nextDraggingEnded() {
    return await firstValueFrom(this.observeIsDragging().pipe(filter(isDragging => !isDragging)));
  }
}
